import React from 'react';
import CommonPagination from '../../components/common/CommonPagination';
import PAGINATION_CONFIG from '../../config/paginationConfig';

const WorksheetWorklistGrid = ({
  items = [],
  totalCount = 0,
  pageNumber = 1,
  pageSize = PAGINATION_CONFIG.defaultPageSize,
  totalPages = 1,
  onPageChange,
  onPageSizeChange,
  isLoading,
  selectedTestIds = [],
  telemetryMap = {},
  onSelectTest,
  onSelectAll,
  onOpenDetail,
  onStartWork,
  onHoldWork,
  onResumeWork,
  onCompleteWork,
  onPrintSingle,
  onViewAudit
}) => {
  const allSelected = items.length > 0 && items.every((i) => selectedTestIds.includes(i.testId));

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case 'STAT':
        return <span className="ws-badge badge-stat">⚡ STAT</span>;
      case 'Urgent':
        return <span className="ws-badge badge-urgent">🔥 Urgent</span>;
      default:
        return <span className="ws-badge badge-routine">Routine</span>;
    }
  };

  if (isLoading) {
    return (
      <div className="ws-table-card">
        <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
          <div style={{ fontSize: '28px', marginBottom: '12px' }}>🔄</div>
          <div style={{ fontWeight: 600 }}>Loading laboratory specimens...</div>
          <div style={{ fontSize: '12px', marginTop: '4px' }}>Querying active pathology worklist &amp; TAT metrics</div>
        </div>
      </div>
    );
  }

  if (!items || items.length === 0) {
    return (
      <div className="ws-table-card">
        <div style={{ padding: '60px 20px', textAlign: 'center', color: '#64748b' }}>
          <div style={{ fontSize: '36px', marginBottom: '10px' }}>📋</div>
          <h3 style={{ margin: '0 0 6px 0', color: '#1e293b' }}>No pending specimens found</h3>
          <p style={{ margin: '0 0 16px 0', fontSize: '13px' }}>
            There are no laboratory samples matching the selected filters.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="ws-table-card">
      <div className="ws-table-wrapper">
        <table className="ws-table">
          <thead>
            <tr>
              <th style={{ width: '38px', textAlign: 'center' }}>
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={(e) => onSelectAll(e.target.checked)}
                  title="Select all on this page"
                />
              </th>
              <th style={{ width: '80px' }}>Priority</th>
              <th style={{ width: '100px' }}>Sample / Barcode</th>
              <th style={{ width: '100px' }}>Lab No</th>
              <th>Patient Demographics</th>
              <th>Investigation / Tests</th>
              <th style={{ width: '150px' }}>Analyzer / Bench</th>
              <th style={{ width: '110px' }}>Sample Type</th>
              <th style={{ width: '130px' }}>Collection Time</th>
              <th style={{ width: '180px', textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((row) => {
              const isSelected = selectedTestIds.includes(row.testId);
              return (
                <tr key={row.testId} className={isSelected ? 'row-selected' : ''}>
                  <td style={{ textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => onSelectTest(row.testId)}
                    />
                  </td>
                  <td>{getPriorityBadge(row.priority)}</td>
                  <td>
                    <div style={{ fontWeight: 700, color: '#0f172a' }}>{row.sampleNo}</div>
                    {row.barcodeNo && row.barcodeNo !== row.sampleNo && (
                      <div style={{ fontSize: '11px', color: '#64748b', fontFamily: 'monospace' }}>
                        {row.barcodeNo}
                      </div>
                    )}
                  </td>
                  <td>
                    <div style={{ fontWeight: 600, color: '#0284c7' }}>{row.labNo}</div>
                  </td>
                  <td>
                    <div style={{ fontWeight: 700, color: '#0f172a' }}>{row.patientName}</div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>
                      {row.age || '-'} / {row.gender || '-'} • UHID: {row.patientId}
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                      <span
                        className="ws-badge"
                        style={{ background: '#e0f2fe', color: '#0369a1', fontWeight: 700 }}
                      >
                        {row.testName}
                      </span>
                      {row.totalTestCount > 1 && (
                        <span
                          className="ws-badge badge-routine"
                          title="Patient has multiple ordered tests"
                        >
                          +{row.totalTestCount - 1} more
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                      {row.sectionName}
                    </div>
                  </td>
                  <td>
                    {(() => {
                      const tel = telemetryMap[row.testId];
                      if (tel) {
                        const isCrit = tel.hasCriticalAlert || tel.highestSeverity === 'CRITICAL';
                        const isReady = tel.machineStatus === 'Machine Result Ready';
                        const isReview = tel.machineStatus === 'Technician Review Required';

                        return (
                          <div>
                            <span style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              fontSize: '11px',
                              fontWeight: 700,
                              background: isCrit ? '#fee2e2' : isReady ? '#dcfce7' : isReview ? '#fef3c7' : '#eff6ff',
                              color: isCrit ? '#b91c1c' : isReady ? '#166534' : isReview ? '#b45309' : '#1d4ed8',
                              border: `1px solid ${isCrit ? '#fca5a5' : isReady ? '#86efac' : isReview ? '#fde68a' : '#bfdbfe'}`
                            }}>
                              <span>🤖 {tel.analyzerCode}</span>
                            </span>
                            <div style={{ fontSize: '10px', color: '#64748b', marginTop: '2px' }}>
                              {tel.machineStatus}
                            </div>
                          </div>
                        );
                      }
                      return <span style={{ fontSize: '11px', color: '#94a3b8' }}>Manual Bench</span>;
                    })()}
                  </td>
                  <td>
                    <span className="ws-badge" style={{ background: '#f1f5f9', color: '#334155' }}>
                      {row.sampleType || 'Blood'}
                    </span>
                  </td>
                  <td>
                    {row.sampleCollectionDate ? (
                      <div style={{ fontSize: '12px', color: '#1e293b' }}>
                        <div style={{ fontWeight: 600 }}>
                          {new Date(row.sampleCollectionDate).toLocaleDateString([], { day: '2-digit', month: 'short' })}
                        </div>
                        <div style={{ fontSize: '11px', color: '#64748b' }}>
                          {new Date(row.sampleCollectionDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                    ) : (
                      <span style={{ fontSize: '11px', color: '#94a3b8', fontStyle: 'italic' }}>
                        Not Collected
                      </span>
                    )}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', justifyContent: 'flex-end' }}>
                      {/* Info Icon Button */}
                      <button
                        type="button"
                        className="ws-icon-btn-info"
                        onClick={() => onOpenDetail(row.testId)}
                        title="View Specimen Details & Timeline"
                        aria-label="View Details"
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10" />
                          <line x1="12" y1="16" x2="12" y2="12" />
                          <line x1="12" y1="8" x2="12.01" y2="8" />
                        </svg>
                      </button>

                      {/* Print Single Button */}
                      <button
                        type="button"
                        className="ws-btn ws-btn-primary ws-btn-sm"
                        onClick={() => onPrintSingle(row.testId)}
                        title="Print / Export PDF for this specimen"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                      >
                        🖨️ PDF / Print
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Standard LIMS Centralized Pagination */}
      <div className="sw-pagination-wrap" style={{ background: '#ffffff', borderTop: '1px solid #e2e8f0' }}>
        <CommonPagination
          page={pageNumber}
          pageSize={pageSize}
          totalRecords={totalCount}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          loading={isLoading}
        />
      </div>
    </div>
  );
};

export default WorksheetWorklistGrid;
